import { useEffect, useRef } from 'react'

import {
  bindAnchoredOverlayPosition,
  type DatePickerAnchorOptions,
} from '../DatePicker/anchorPositioning'

import { type DatePickerAnchorState } from './useWorkExperienceRoleHandlers/datePickerHelpers'

type ScrollRootRef = React.RefObject<HTMLElement | null> | undefined

/** Keep a portaled DatePicker anchored to its trigger while any ancestor scrolls. */
export function useDatePickerAnchorPosition<T extends DatePickerAnchorState>(
  datePickerState: T | null,
  dateFieldRefs: React.MutableRefObject<{ [key: string]: HTMLElement | null }>,
  setDatePickerState: React.Dispatch<React.SetStateAction<T | null>>,
  datePickerRef: React.RefObject<HTMLElement | null>,
  options?: DatePickerAnchorOptions,
  scrollRootRef?: ScrollRootRef,
): void {
  const triggerKey = datePickerState?.triggerKey ?? null
  const optionsRef = useRef(options)
  optionsRef.current = options

  useEffect(() => {
    if (!triggerKey) return

    return bindAnchoredOverlayPosition({
      getTrigger: () => dateFieldRefs.current[triggerKey] ?? null,
      getPicker: () => datePickerRef.current,
      options: optionsRef.current,
      extraScrollTargets: scrollRootRef?.current ? [scrollRootRef.current] : [],
      onPosition: (position) => {
        setDatePickerState((prev) => {
          if (!prev || prev.triggerKey !== triggerKey) return prev
          const nextPosition = {
            top: position.top,
            left: position.left,
            width: position.width,
          }
          const current = prev.position
          if (
            current.top === nextPosition.top &&
            current.left === nextPosition.left &&
            current.width === nextPosition.width
          ) {
            return prev
          }
          return { ...prev, position: nextPosition }
        })
      },
    })
  }, [triggerKey, dateFieldRefs, datePickerRef, scrollRootRef, setDatePickerState])
}
